import React, { useState } from 'react';
import clsx from 'clsx';
import { SLogoSoundBarOverlay } from '@components/SoundBar/SLogoSoundBarOverlay';
import { SoundBarOverlay } from '@components/SoundBar/SoundBarOverlay';
import { Waveform } from '@components/Waveform/Release/Waveform';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { EditArtistPickMenuItem } from '@components/basics/Dropdowns/MenuItems/EditArtistPickMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { EditArtistPickModal } from '@components/modals/EditArtistPickModal';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  ArtistLatestReleaseDocument,
  gql,
  SetArtistPickedReleaseDocument,
  useQuery,
} from '@graphql/reactQuery';
import { useGetDominantBackgroundColor } from '@hooks/useGetDominantBackgroundColor';
import { usePlayButton } from '@hooks/usePlayButton';
import { DEFAULT_DOMINANT_BACKGROUND_COLOR } from '@utils/constants';
import { formatDateFromTime, getStaticCoverImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { ProfileTopSectionSkeleton } from './ProfileTopSectionSkeleton';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ArtistLatestRelease($artistId: UUID!, $filter: ArtistReleasesFilter) {
    artist(id: $artistId) {
      id
      pickedRelease {
        id
        title
        createdAt
        artist {
          id
          name
          webappUri
        }
        staticCoverImage {
          id
          url
        }
        coverImage {
          id
          url
          dominantColor
        }
        track {
          id
          normalizedPeaks
          duration
        }
        webappUri
        mintStartTime
        ...WaveformCommentsReleaseInfo
      }
      releases(pagination: { first: 1, sort: DESC }, filter: $filter) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            id
            title
            artist {
              id
              name
              webappUri
            }
            staticCoverImage {
              id
              url
            }
            coverImage {
              id
              url
              dominantColor
            }
            track {
              id
              normalizedPeaks
              duration
            }
            webappUri
            mintStartTime
            ...WaveformCommentsReleaseInfo
          }
        }
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [SetArtistPickedReleaseDocument],
  refetch: [ArtistLatestReleaseDocument],
});

export const ArtistPick = ({ artistId }: { artistId: string }) => {
  const { tablet } = useMediaQuery();
  const { openModal } = ModalContext.useContainer();
  const { authUser } = useAuth();

  const isAuthUser = authUser?.artist?.id === artistId;

  const { data: artistReleasesData, isLoading: isLatestReleaseLoading } = useQuery(
    ArtistLatestReleaseDocument,
    {
      variables: { artistId },
      staleTime: 0,
      cacheTime: 0,
    },
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const artistReleases = artistReleasesData?.data?.artist?.releases;
  const featuredRelease = artistReleasesData?.data?.artist?.pickedRelease;

  const artistPickRelease = featuredRelease ?? artistReleases?.edges?.[0]?.node;

  const {
    isPlaying,
    isLoading: isPlayButtonLoading,
    onPlayClick,
  } = usePlayButton({
    isAlbum: false,
    trackId: artistPickRelease?.track?.id ?? '',
  });

  const dominantColor =
    artistPickRelease?.coverImage.dominantColor === '#FFFFFF' ||
    !artistPickRelease?.coverImage.dominantColor
      ? DEFAULT_DOMINANT_BACKGROUND_COLOR
      : artistPickRelease.coverImage.dominantColor;

  const dominantBackgroundColor = useGetDominantBackgroundColor({ dominantColor });

  return (
    <div className="mt-6 flex flex-col gap-2 bg-white px-5 md:mt-12 lt:px-0">
      <div className="font-title text-title-m font-medium md:text-title-l">Artist Pick</div>

      {isLatestReleaseLoading ? (
        <ProfileTopSectionSkeleton />
      ) : (
        artistPickRelease && (
          <div
            className="align-center relative flex h-[100px] w-full items-center overflow-hidden rounded-lg px-4 md:h-40"
            style={{ backgroundColor: dominantBackgroundColor }}
          >
            <div className="absolute left-0 top-0 w-full overflow-hidden">
              <SoundBarOverlay className="relative h-full w-full overflow-hidden" />
            </div>
            <div className="absolute left-[300px] top-0 overflow-hidden">
              <SLogoSoundBarOverlay className="relative overflow-hidden" />
            </div>
            <div className="color-white absolute right-2 top-2 z-above1 md:top-2">
              <ThreeDotsDropdown
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
                variant="inverted"
              >
                {isAuthUser && (
                  <EditArtistPickMenuItem
                    label="Edit Artist Pick"
                    onClick={() =>
                      openModal(ModalType.EDIT_FEATURED_SOUND, {
                        body: (
                          <EditArtistPickModal
                            artistId={artistId}
                            artistPickReleaseId={artistPickRelease.id}
                          />
                        ),
                      })
                    }
                  />
                )}
                <AddToShelfMenuItem releaseId={artistPickRelease.id} source="Collector Profile" />
                <ShareMenuItem releaseId={artistPickRelease.id} source="Collector Profile" />
              </ThreeDotsDropdown>
            </div>

            <div className="relative flex w-full gap-2">
              <div className="group relative h-[68px] w-[68px] transition-opacity duration-300 ease-in-out md:h-[127px] md:w-[127px]">
                <div className="h-[68px] w-[68px] overflow-hidden rounded-md md:h-[127px] md:w-[127px]">
                  <NextFutureImage
                    alt="featured release image"
                    src={getStaticCoverImage({
                      coverImage: artistPickRelease.coverImage.url,
                      staticCoverImage: artistPickRelease.staticCoverImage?.url,
                    })}
                    width={tablet ? 127 : 68}
                    height={tablet ? 127 : 68}
                  />
                </div>
                <div
                  className={clsx(
                    'absolute left-3.5 top-3.5 md:left-11 md:top-11',
                    tablet &&
                      'opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100',
                    !tablet && 'hidden',
                  )}
                >
                  <PlayButton
                    color="white"
                    playing={isPlaying}
                    onClick={onPlayClick}
                    size="s"
                    loading={isPlayButtonLoading}
                  />
                </div>
              </div>

              <div className="flex w-full flex-col gap-4">
                <div className="flex w-[194px] flex-col gap-1 md:w-auto md:max-w-[60%]">
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-s text-white">
                    <AccessibleLink pathnameUri={webappUriPath(artistPickRelease.artist.webappUri)}>
                      {artistPickRelease.artist.name}
                    </AccessibleLink>
                  </div>
                  <div className="flex gap-2 whitespace-nowrap font-title text-title-s font-medium text-white">
                    {isPlaying && <PlayIndicatorBars smallBars={!tablet} />}
                    <AccessibleLink
                      className="overflow-hidden text-ellipsis"
                      pathnameUri={webappUriPath(artistPickRelease.webappUri)}
                    >
                      {artistPickRelease.title}
                    </AccessibleLink>
                  </div>
                  <div className="mt-2 flex items-center gap-1 md:mt-2 md:h-[18px]">
                    <p className="font-base text-base-s text-white opacity-60">
                      Release Date {formatDateFromTime({ time: artistPickRelease.mintStartTime })}
                    </p>
                  </div>
                </div>

                {tablet && (
                  <Waveform
                    track={artistPickRelease.track}
                    releaseFrag={artistPickRelease}
                    isTrackDisabled={false}
                    showComments={false}
                    showBottomHalf={false}
                    enableTouchHandler
                  />
                )}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};
